import { firebase } from "./firebase-config";
import axiosInstance from "./axios";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  getIdToken,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getLoginToken } from "./storage";
import {
  BaseUrl,
  DOMAINURL,
} from "./config";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { incrementNotificationLimits } from "./apiservices/employee";
import { logUserActionBackground, USER_ACTION_EVENTS } from "./userActionLog";
// const firestore = firebase.firestore();
// const settings = { timestampsInSnapshots: true };
// firestore.settings(settings);

/**
 * so this function is called when the authentication state changes
 * in the application, a side effect of that is that we need to get
 * the rest of the user data from the user collection, that is
 * done with the _handleAuthedUser callback
 */
const auth = getAuth();
const storage = getStorage();

export const authCheck = async (_handleAuthedUser) => {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        return resolve(await _handleAuthedUser(user));
      } else {
        _handleAuthedUser(null);
        return resolve(null);
      }
    });
  });
};

/**
 *
 * @param {*} email
 * @param {*} password
 */
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    const userDocRef = doc(firebase, "users", user.uid);
    const [token, userDoc] = await Promise.all([
      user.getIdToken(),
      getDoc(userDocRef),
    ]);
    const userData = userDoc.exists() ? userDoc.data() : {};
    const role = userData?.role;
    const existingSessionId = userData?.sessionId || null;
    /** Same shape as getCurrentUserQuick — lets App skip a duplicate Firestore read after sign-in */
    const userDataWithId = userDoc.exists()
      ? { ...userData, id: user.uid }
      : { id: user.uid };

    logUserActionBackground({
      userId: user.uid,
      eventName: USER_ACTION_EVENTS.LOGIN,
      email: user.email || "",
    });

    return {
      userId: user?.uid,
      user,
      token,
      role,
      existingSessionId,
      userData: userDataWithId,
    };
  } catch (error) {
    return error;
  }
};

/**
 * Update user session ID in Firestore
 * @param {string} userId 
 * @param {string|null} sessionId 
 */
export const updateUserSession = async (userId, deviceInfo, forceLogin = false) => {
  const isLogoutRequest = !deviceInfo;

  const res = await axiosInstance.post(`${BaseUrl}updateUserSession`, {
    userId,
    deviceInfo,
    forceLogin,
    clearSession: isLogoutRequest
  });

  return res.data;

};
/**
 * Merge Stripe/subscription fields from the company owner doc (employees, clients, etc.).
 */
export const mergeCompanyUserIntoUserData = async (userData) => {
  if (!userData?.companyId) return userData;
  const raw = userData?.role;
  const roles = Array.isArray(raw) ? raw : raw ? [raw] : [];
  if (roles.includes("companyUser") || roles.includes("superUser")) return userData;

  const compUserDocRef = doc(firebase, "users", userData.companyId);
  const docCompSnap = await getDoc(compUserDocRef);
  const compUserData = docCompSnap.data();
  return {
    ...userData,
    customer: compUserData?.customer,
    subscriptions: compUserData?.subscriptions || null,
  };
};

/**
 * Token + own user doc only (single Firestore read). Use mergeCompanyUserIntoUserData when needed.
 * Faster than getCurrentUser() for the post-login path so the UI can leave the login screen sooner.
 */
export const getCurrentUserQuick = async () => {
  const user = auth.currentUser;
  let token = null;
  let userData = {};

  if (user) {
    token = await getIdToken(user);

    const userDocRef = doc(firebase, "users", user.uid);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      userData = { ...docSnap.data(), id: user.uid };
    } else {
      console.log("No such document!", user.uid);
    }
  }
  return { user, userData, token };
};

/**
 *
 * @returns data of current logged in user from users collection
 */
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  let token = null;
  let userData = {};

  if (user) {
    token = await getIdToken(user);

    const userDocRef = doc(firebase, "users", user.uid);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      userData = { ...docSnap.data(), id: user.uid };
      userData = await mergeCompanyUserIntoUserData(userData);
    } else {
      console.log("No such document!", user.uid);
    }
  }
  return { user, userData, token };
};

export const getCurrentUserToken = async (user) => {
  let token = await user.getIdToken();
  return token;
};
/**
 * Log out user
 */
export const logOut = () => {
  return signOut(auth);
};

/**
 * Registration-only: find one active (non-deleted, non-canceled) user by email.
 * Uses a single indexed query instead of scanning the whole users collection.
 */
export const findActiveUserByEmailForRegistration = async (email) => {
  const trimmed = typeof email === "string" ? email.trim() : "";
  if (!trimmed) return null;
  try {
    const q = query(
      collection(firebase, "users"),
      where("email", "==", trimmed),
      limit(25)
    );
    const snap = await getDocs(q);
    for (const docSnap of snap.docs) {
      const item = { id: docSnap.id, ...docSnap.data() };
      const isDeleted = item?.deleted === true;
      const isCanceled = item?.status === "canceled";
      if (!isDeleted && !isCanceled) return item;
    }
    return null;
  } catch (e) {
    console.error("[findActiveUserByEmailForRegistration]", e);
    return null;
  }
};

export const emailLinkSent = async (email, firstname) => {
  const trimmed =
    typeof email === "string" ? email.trim().toLowerCase() : "";
  if (!trimmed) {
    return Promise.reject(new Error("Email is required"));
  }
  const uid = auth?.currentUser?.uid || null;
  return axiosInstance.post(`${BaseUrl}sendEmailVerifyLink`, {
    email: trimmed,
    displayName: firstname,
    ...(uid ? { uid } : {}),
  });
};

export const forgotPasswordAgainemailLinkSent = async (email) => {
  return axiosInstance.post(`${BaseUrl}sendResetPasswordLink`, {
    email,
    displayName: null,
    role: null,
    employeerole: null,
  });
};

export const updateEmailResendLink = async (userId, role) => {
  if (role === "ClientTeam") {
    return axiosInstance.post(`${BaseUrl}employeeEmailVerifyLink`, {
      oldEmail: null,
      newEmail: null,
      displayName: null,
      userId: userId,
    });
  } else {
    return axiosInstance.post(`${BaseUrl}emailVerifyLink`, {
      oldEmail: null,
      newEmail: null,
      displayName: null,
      userId: userId,
    });
  }
};

/**
 *
 * @param {*} userInfo.lastName
 * @param {*} userInfo.firstName
 * @param {*} userInfo.email
 * @param {*} userInfo.password
 */

export const registerUser = async (userInfo, existingUser = null) => {
  let newcreateduser = {};
  try {
    const {
      companyName,
      firstName,
      lastName,
      email: rawEmail,
      username,
      phoneNumber,
      website,
      peoples,
      industry,
      password,
      confirmPassword,
      createdAt,
      role,
      teamAdminSeat,
      promocode,
      companyAddress,
      companyCity,
      companyState,
      companyZip,
    } = userInfo;
    const email =
      typeof rawEmail === "string" ? rawEmail.trim().toLowerCase() : "";
    if (!email) {
      return { status: false, message: "Email is required" };
    }
    const normalizedCompanyCity = companyCity?.trim() || "";
    const normalizedCompanyState = companyState?.trim() || "";
    const priceId =
      process.env.REACT_APP_CUSTOM_ENV === "prod"
        ? "price_1RsdExHPX5Uwv3eVvXeq6n3a"
        : process.env.REACT_APP_CUSTOM_ENV === "dev"
          ? "price_1Roe9PH54HzvHwSdFEwomMT8"
          : "price_1RoeHcHG9KfCFcBtNO24WZi1";

    if (existingUser && existingUser !== null) {
      const userRef = doc(firebase, "users", existingUser?.id);
      await updateDoc(userRef, {
        email: email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        role: role || existingUser?.role || "Employee",
        teamAdminSeat: teamAdminSeat ?? existingUser?.teamAdminSeat ?? false,
        phoneNumber: phoneNumber || "",
        companyName: companyName || "",
        website: website || "",
        peoples: peoples || "",
        industry: industry || "",
        password: password || "",
        confirmPassword: confirmPassword || "",
        promocode: promocode || "",
        companyAddress: companyAddress || "",
        companyCity: normalizedCompanyCity,
        companyState: normalizedCompanyState,
        companyZip: companyZip || "",
        updatedAt: new Date(),
        verified: false,
      });

      void axiosInstance
        .post(`${BaseUrl}sendEmailVerifyLink`, {
          email,
          displayName: companyName,
          uid: existingUser?.id,
        })
        .catch((err) =>
          console.error("[sendEmailVerifyLink] (background)", err?.message || err)
        );

      logUserActionBackground({
        userId: existingUser?.id,
        eventName: USER_ACTION_EVENTS.REGISTER,
        email,
      });

      return {
        status: true,
        message:
          "Existing user updated and verification email sent successfully.",
        existingUser,
        firstName,
        lastName,
      };
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      userInfo.password
    );
    newcreateduser = userCredential;

    const [firestoreWrite, subscriptionRes] = await Promise.all([
      setDoc(doc(firebase, "users", newcreateduser.user.uid), {
        email: email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        role: role || "Employee",
        teamAdminSeat: teamAdminSeat ?? false,
        createdAt: createdAt || "",
        phoneNumber: phoneNumber || "",
        companyName: companyName || "",
        website: website || "",
        peoples: peoples || "",
        industry: industry || "",
        password: password || "",
        confirmPassword: confirmPassword || "",
        promocode: promocode || "",
        companyAddress: companyAddress || "",
        companyCity: normalizedCompanyCity,
        companyState: normalizedCompanyState,
        companyZip: companyZip || "",
        verified: false,
      }),

      axiosInstance.post(`${BaseUrl}createSubscription`, {
        email,
        name: companyName,
        phone: phoneNumber,
        priceId: priceId,
        promocode: promocode,
      }),

      // axiosInstance.post(`${BaseUrl}createSubscriptionSchedule`, {
      //   email,
      //   name: companyName,
      //   phone: phoneNumber,
      // }),
    ]);

    void axiosInstance
      .post(`${BaseUrl}sendEmailVerifyLink`, {
        email,
        displayName: companyName,
        uid: newcreateduser.user.uid,
      })
      .catch((err) =>
        console.error("[sendEmailVerifyLink] (background)", err?.message || err)
      );

    logUserActionBackground({
      userId: newcreateduser.user.uid,
      eventName: USER_ACTION_EVENTS.REGISTER,
      email,
    });

    if (subscriptionRes?.data?.status === true) {
      console.log("Subscription Created:", subscriptionRes.data);
      return { ...newcreateduser.user, firstName, lastName };
    }
  } catch (error) {
    console.error("[registerUser error]", {
      code: error.code,
      message: error.message,
    });

    if (error.code === "auth/email-already-in-use") {
      return { status: false, message: "Email is already registered" };
    }

    return { status: false, message: error.message || "Something went wrong" };
  }
};

/**
 * register new user to firebase auth and users collection (not used for now manage from cloud function)
 * @param {*} newUser
 * @returns
 */
export async function firebasesignupUser(newUser) {
  try {
    const userRef = doc(firebase, "users", newUser.email);
    const docSnap = await getDoc(userRef);

    if (docSnap.exists()) {
      return { status: false, message: "This username is already taken" };
    } else {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );
      const newCreatedUser = userCredential.user;

      const { email, firstName, lastName, role, createdAt, phonenumber } =
        newUser;

      await setDoc(doc(firebase, "users", newCreatedUser.uid), {
        email: email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        role: role || "",
        createdAt: createdAt || "",
        phoneNumber: phonenumber || "",
      });

      return { ...newCreatedUser, firstName, lastName };
    }
  } catch (error) {
    return { status: false, error: error.message };
  }
}

/**
 * Sent Forgot password email for give email user
 * @param {*} email
 * @param {*} onSuccess
 * @param {*} onError
 */
export const forgotPasswordUser = async (email, onSuccess, onError) => {
  try {
    const trimmed = typeof email === "string" ? email.trim() : email;
    // Do not query Firestore here: unauthenticated users have no read access to `users`
    // (see firestore.rules). Validation runs in sendResetPasswordLink (Admin SDK).
    await axiosInstance.post(
      `${BaseUrl}sendResetPasswordLink`,
      { email: trimmed },
      { skipAuthRedirect: true }
    );

    onSuccess?.({ status: true, message: "Password reset email sent!" });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong. Please try again.";
    if (onError) {
      onError({ status: false, message: errorMessage });
    }
  }
};

export const forgotPasswordAdmin = async (
  email,
  phoneNumber,
  companyId,
  onSuccess,
  onError
) => {
  try {
    const userQuery = query(
      collection(firebase, "users"),
      where("email", "==", email)
    );
    const userSnapshot = await getDocs(userQuery);

    if (userSnapshot.empty) {
      if (onError) {
        onError({ status: false, message: "No User found with this email" });
      }
      return;
    }

    let user = {};
    userSnapshot.forEach((doc) => {
      user = doc.data();
    });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(
      `${BaseUrl}sendResetPasswordLink`,
      {
        email,
        displayName: user.firstName || user.name,
        role: user.role,
        employeerole: user.employeerole,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    if (onSuccess) {
      onSuccess({
        status: true,
        message: "Password reset link sent by email and SMS (if on file).",
      });
    }
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message || error.message;
    if (onError) {
      onError({ status: false, message: errorMessage });
    }
  }
};

export const ResendCMSLink = async (
  email,
  phoneNumber,
  firstName,
  password,
  role,
  onSuccess,
  onError
) => {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(
      `${BaseUrl}sendManagedUserPasswordResetLink`,
      {
        email,
        richAppInvite: false,
        inviteFirstName: firstName,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );
    onSuccess?.({ status: true, message: "Resend Invitation sent!" });
    return { status: true, message: "Invitation sent with secure link" };
  } catch (error) {
    if (onError) {
      onError({ status: false, message: error.message });
    }
  }
};

export const ResendAppLink = async (
  email,
  phoneNumber,
  firstName,
  password,
  contactName,
  companyName,
  companyId,
  onSuccess,
  onError
) => {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(
      `${BaseUrl}sendManagedUserPasswordResetLink`,
      {
        email,
        richAppInvite: true,
        inviteFirstName: firstName,
        contactName,
        companyName,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    if (companyId) {
      await incrementNotificationLimits(companyId);
    }

    onSuccess?.({ status: true, message: "Resend Invitation sent!" });
    return { status: true, message: "Invitation sent with secure link" };
  } catch (error) {
    if (onError) {
      onError({ status: false, message: error.message });
    }
    return { status: false, message: error.message };
  }
};

/**
 * Returns Current login user profile data
 */
export const getUserProfile = async (token, onSuccess, onError) => {
  try {
    const user = auth.currentUser;

    if (user) {
      const userRef = doc(firebase, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        onSuccess &&
          onSuccess({
            status: true,
            data: { ...docSnap.data(), id: user.uid },
          });
      } else {
        onError && onError({ status: false, data: "No such document!" });
      }
    } else {
      onError && onError({ status: false, data: "No user logged in!" });
    }
  } catch (error) {
    onError &&
      onError({ status: false, data: "Error retrieving user profile!", error });
  }
};

/**
 *
 * @param {*} blob
 */
export const uploadImage = (blob) => {
  return new Promise((resolve, reject) => {
    const currentUserId = auth.currentUser?.uid;

    const imageRef = ref(
      storage,
      `${currentUserId}/${new Date().getTime()}-${currentUserId}.jpeg`
    );

    const uploadTask = uploadBytesResumable(imageRef, blob);

    uploadTask.on(
      "state_changed",
      (snapshot) =>
        console.log((snapshot.bytesTransferred / snapshot.totalBytes) * 100),

      (error) => {
        console.log("error", error);
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const metadata = uploadTask.snapshot.metadata;
          resolve({
            url: downloadURL,
            contentType: metadata.contentType,
            name: metadata.name,
            size: metadata.size,
          });
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

/**
 * Re-authenticate the currently signed-in user with their email/password.
 * Used by the sensitive-action re-authentication gate (reAuthService / ReAuthModal).
 * Throws a Firebase Auth error on failure (wrong password, too-many-requests, etc.).
 */
export const reauthenticateUser = async (password) => {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error("No active session found.");
    err.code = "auth/user-not-found";
    throw err;
  }
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
};

export const updatepasswordem = (code, newPassword, onSuccess, onError) => {
  const params = new URLSearchParams(window.location.search);
  const emailFromQuery = params.get("email");

  confirmPasswordReset(auth, code, newPassword)
    .then(() => {
      onSuccess && onSuccess({ status: true, message: "Password Updated!" });
    })
    .catch(async (error) => {
      let friendlyMessage = "Something went wrong. Please try again.";

      switch (error.code) {
        case "auth/expired-action-code":
          if (emailFromQuery) {
            try {
              await axiosInstance.post(
                `${BaseUrl}sendResetPasswordLink`,
                { email: emailFromQuery },
                { skipAuthRedirect: true }
              );
              friendlyMessage =
                "This reset link expired. We sent a new link to your email and mobile number on file.";
            } catch {
              friendlyMessage =
                "This reset link has expired. Please request a new one from the login page.";
            }
          } else {
            friendlyMessage =
              "This reset link has expired. Please request a new one from the login page.";
          }
          break;
        case "auth/invalid-action-code":
          friendlyMessage =
            "This reset link is invalid or has already been used.";
          break;
        case "auth/user-not-found":
          friendlyMessage =
            "No account found with this email. Please check or sign up.";
          break;
        case "auth/weak-password":
          friendlyMessage =
            "Password is too weak. Please choose a stronger password.";
          break;
        default:
          friendlyMessage = "Unable to reset your password. Please try again.";
      }
      onError && onError({ status: false, message: friendlyMessage });
    });
};
